import Flex from "@components/Flex";
import DashboardChartFilters from "@components/DashboardChartFilters";
import DashboardChartResponsibleFilter from "@components/DashboardChartFilters/DashboardChartResponsibleFilter";
import DashboardChartWrapper from "@components/DashboardChartWrapper";
import DashboardDefaultChartEmptyState from "@components/EmptyStates/DashboardDefaultChartEmptyState";
import { Text } from "nectar-ui";
import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { getResponsiblesState } from "@selectors/responsiblesSelectors";
import { sendEventChartUsed } from "@services/dashboardAnalyticEventService";
import {
  dashboardChartFilterAndActionTypes,
  dashboardEventTypes,
} from "@utils/constants/dashboard/analyticDashboardEvents";
import { DASHBOARD_CHART_TYPES } from "@utils/constants/dashboard/dashboardModuleConstants";

import { QueryLoaderWrapper } from "../../../components/Loaders";
import useDefaultResponsibleFilter from "../../../hooks/useDefaultResponsibleFilter";
import {
  CustomChartStyleWrapper,
  StripedColorStackedBarChartWrapper,
  ToggletipText,
} from "../../../style";
import { getBarChartOptions, MAXIMUM_RESPONSIBLE_NUMBER } from "./helpers";
import { CustomTooltipWithoutHeaderChartWrapper } from "./style";
import useActiveLawsuitsByResponsibleBarChartQuery from "./useActiveLawsuitsByResponsibleBarChartQuery";

const ActiveLawsuitByResponsibleBarChart = () => {
  const allResponsibles = useSelector(getResponsiblesState);

  const DEFAULT_RESPONSIBLE_FILTER = useDefaultResponsibleFilter(MAXIMUM_RESPONSIBLE_NUMBER);
  const [responsibleFilter, setResponsibleFilter] = useState(DEFAULT_RESPONSIBLE_FILTER);
  const [queryStatus, activeLawsuitByResponsibleResponse] =
    useActiveLawsuitsByResponsibleBarChartQuery(responsibleFilter, allResponsibles);

  const setResponsibleFilterAndSendMixPanelEvent = (filter: string[]) => {
    setResponsibleFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.ACTIVE_LAWSUITS_BY_RESPONSIBLE,
      dashboardChartFilterAndActionTypes.RESPONSIBLE
    );
  };

  const CHART_HEIGHT = 350;
  const CHART_OFFSET = 15;

  const { series, names, noData } = activeLawsuitByResponsibleResponse;

  const options = getBarChartOptions(names);

  const shouldRenderEmptyState = noData;

  const header = {
    title: "Distribuição de processos e casos ativos por responsável",
    description:
      "Confira a quantidade de processos e casos ativos hoje para analisar se há necessidade de redistribuição de demanda entre os responsáveis.",
  };

  const toggleTip = {
    title: "Processos e casos ativos por responsável",
    text: (
      <ToggletipText>
        No gráfico, são considerados aqueles que ainda não foram encerrados no Astrea, portanto
        estão ativos hoje.
      </ToggletipText>
    ),
    onClickCallback: () => {
      sendEventChartUsed(
        DASHBOARD_CHART_TYPES.ACTIVE_LAWSUITS_BY_RESPONSIBLE,
        dashboardEventTypes.TAB_OPEN
      );
    },
    callToAction: {
      text: "Acessar central de ajuda",
      callback: () => {
        sendEventChartUsed(
          DASHBOARD_CHART_TYPES.ACTIVE_LAWSUITS_BY_RESPONSIBLE,
          dashboardChartFilterAndActionTypes.HELP_CENTER_CLICK
        );
        window.open(
          "https://astrea.aurum.com.br/pt-BR/articles/8043391-como-analisar-a-distribuicao-de-processos-e-casos-por-responsavel",
          "_blank"
        );
      },
    },
  };

  return (
    <DashboardChartWrapper
      title={header.title}
      description={header.description}
      toggleTip={toggleTip}
    >
      <DashboardChartFilters>
        <Flex align="center">
          <Text color="gray500" semiBold noMargin style={{ marginRight: 8 }}>
            Hoje
          </Text>
          <DashboardChartResponsibleFilter
            currentFilter={responsibleFilter}
            setFilter={setResponsibleFilterAndSendMixPanelEvent}
            maximumSelected={MAXIMUM_RESPONSIBLE_NUMBER}
          />
        </Flex>
      </DashboardChartFilters>
      {shouldRenderEmptyState ? (
        <DashboardDefaultChartEmptyState
          title="Nenhum processo ou caso ativo encontrado"
          subtitle="Verifique o filtro selecionado."
          chartId={DASHBOARD_CHART_TYPES.ACTIVE_LAWSUITS_BY_RESPONSIBLE}
          link="https://astrea.aurum.com.br/pt-BR/articles/8043391-como-analisar-a-distribuicao-de-processos-e-casos-por-responsavel"
        />
      ) : (
        <CustomChartStyleWrapper>
          <QueryLoaderWrapper
            queryStatus={queryStatus}
            skeletonComponent={<Skeleton height={CHART_HEIGHT + CHART_OFFSET} />}
          >
            <CustomTooltipWithoutHeaderChartWrapper>
              <StripedColorStackedBarChartWrapper evenColor="primary900">
                <ReactApexChart
                  options={options}
                  series={series}
                  type="bar"
                  height={CHART_HEIGHT}
                  width="95%"
                />
              </StripedColorStackedBarChartWrapper>
            </CustomTooltipWithoutHeaderChartWrapper>
          </QueryLoaderWrapper>
        </CustomChartStyleWrapper>
      )}
    </DashboardChartWrapper>
  );
};

export default ActiveLawsuitByResponsibleBarChart;
